import React, { useRef, useState, useEffect, useMemo } from 'react';
import Timeline from 'react-visjs-timeline';

const Chronicle = ({ proInputs, keyword, initialWindow, onWindowChange }) => {
  const timelineRef = useRef(null);

  const containerRef = useRef(null); // ✅ Add container ref
  const [zoomInterval, setZoomInterval] = useState(null);
  const [isHierarchyCollapsed, setIsHierarchyCollapsed] = useState(false); // ✅ Add state

  // Custom tooltip for touch devices (desktop uses native title hover)
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: '',
    position: { x: 0, y: 0 },
    itemId: null
  });
  const itemsRef = useRef([]); // Ref to access items in touch handler without re-running effect
  const savedWindowRef = useRef(null);
  const isRestoringRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const prevKeywordRef = useRef(keyword);
  const prevInitialWindowRef = useRef(initialWindow); // ✅ Track previous initialWindow
  const isInitialLoadRef = useRef(true); // ✅ Track initial load
  const hasRestoredFromStorageRef = useRef(false); // ✅ Track if restored from localStorage
  const urlWindowAppliedRef = useRef(false); // ✅ Track if URL window was applied

  const options = {
    stack: true,
    horizontalScroll: true,
    verticalScroll: true,
    zoomKey: 'ctrlKey',
    start: Date.now() - 1000 * 60 * 60 * 24 * 60,
    end: Date.now() + 1000 * 60 * 60 * 24 * 21,
    orientation: {
      // axis: 'both',
      axis: 'top',
      item: 'top'
    },
    editable: true,
    // Without a bounded height, vis-timeline auto-grows to fit all content
    // (no internal scrolling), so the whole page scrolls instead and the
    // axis - which the library only keeps fixed within its own internally-
    // scrolling box - scrolls away with it. Constraining the height lets
    // vis-timeline scroll the rows internally while keeping the axis fixed,
    // which is how verticalScroll above is actually meant to work.
    height: 'calc(100vh - 80px)',
    groupOrder: function (a, b) {
      return a.value - b.value;
    }
  };

  // ✅ Only calculate once data is actually loaded
  const isDataLoaded = proInputs && proInputs.length > 0;

  // Memoize all the heavy computation - only recalculate when data is actually loaded
  const { groups, items } = useMemo(() => {
    // ✅ Skip calculation until data is loaded
    if (!isDataLoaded) {
      return { groups: [], items: [] };
    }

    const parseKeywords = keywords => {
      return keywords.split('|').map(group => {
        const andConditions = group
          .trim()
          .split(/\s+/)
          .map(condition => {
            return { type: 'AND', value: condition.toLowerCase() };
          });
        return andConditions;
      });
    };

    const evaluateConditions = (conditions, person) => {
      return conditions.some(andConditions =>
        andConditions.every(condition => {
          return person.includes(condition.value);
        })
      );
    };

    const findChildGroups = (groups, parentCondition2Numbers) => {
      const childGroups = groups.filter(
        group =>
          group.condition5 &&
          group.condition5
            .split(',')
            .map(num => parseInt(num.trim(), 10))
            .some(num => parentCondition2Numbers.has(num))
      );

      const childCondition2Numbers = new Set(
        childGroups.map(group => parseInt(group.condition2, 10))
      );

      if (childGroups.length > 0) {
        const deeperChildCondition2Numbers = findChildGroups(
          groups,
          childCondition2Numbers
        );
        return new Set([
          ...childCondition2Numbers,
          ...deeperChildCondition2Numbers
        ]);
      }

      return parentCondition2Numbers;
    };

    const parsedKeywords = parseKeywords(keyword);

    const timelineProInputs = keyword
      ? proInputs.filter(item => {
          const subject = item.subject.toLowerCase();
          const person = item.person.toLowerCase();
          return (
            subject.includes('timeline') &&
            evaluateConditions(parsedKeywords, person)
          );
        })
      : proInputs.filter(item =>
          item.subject.toLowerCase().includes('timeline')
        );

    const groupProInputs = timelineProInputs.filter(
      item => item.condition1.toLowerCase() === 'group'
    );

    const initialGroupCondition2Numbers = new Set(
      groupProInputs.map(group => parseInt(group.condition2, 10))
    );

    const allGroupCondition2Numbers = findChildGroups(
      proInputs.filter(item => item.condition1.toLowerCase() === 'group'),
      initialGroupCondition2Numbers
    );

    const itemProInputs = proInputs.filter(
      item => item.condition1.toLowerCase() === 'item'
    );

    const finalProInputs = [
      ...new Map(
        [...groupProInputs, ...itemProInputs].map(item => [item._id, item])
      ).values()
    ];

    const timelineGroups = finalProInputs.filter(item =>
      item.condition1.toLowerCase().includes('group')
    );
    const timelineItems = finalProInputs.filter(item =>
      item.condition1.toLowerCase().includes('item')
    );

    const groups = timelineGroups.map(group => ({
      id: parseInt(group.condition2, 10),
      content: group.condition3,
      value: parseInt(group.condition4, 10),
      nestedGroups: group.condition5
        ? group.condition5.split(',').map(char => parseInt(char, 10))
        : undefined,
      treeLevel: parseInt(group.condition6, 10),
      title: group.condition11
    }));

    const items = timelineItems.flatMap(item => {
      const groupNumbers = item.condition7
        .split(',')
        .map(num => parseInt(num.trim(), 10));

      return groupNumbers.map(groupNumber => {
        const message = item.condition3.trim();
        const url = item.condition4.trim();
        const content = url
          ? `<a href="${url}" target="_blank">${message}</a>`
          : message;

        return {
          id: item._id + '-' + groupNumber,
          group: groupNumber,
          content: content,
          start: item.condition8,
          end: item.condition9,
          type: item.condition10.trim().toLowerCase(),
          title: item.condition11,
          style: item.condition12
        };
      });
    });

    // ✅ Mark that initial load calculation is done
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }

    return { groups, items };
  }, [isDataLoaded ? proInputs : null, keyword]);
  // ✅ Only depend on proInputs when data is loaded

  // Keep itemsRef updated with latest items (for touch handler)
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Handle select event - used for touch device tooltip toggle
  const handleSelect = event => {
    // Only handle on touch devices
    if (!('ontouchstart' in window)) return;

    const { items: selectedItems } = event;
    if (selectedItems.length > 0) {
      const itemId = selectedItems[0];
      const selectedItem = itemsRef.current.find(item => item.id === itemId);
      const itemTitle = selectedItem?.title || '';

      if (itemTitle) {
        // Toggle: if same item, hide; otherwise show
        setTooltip(prev => {
          if (prev.visible && prev.itemId === itemId) {
            return {
              visible: false,
              content: '',
              position: { x: 0, y: 0 },
              itemId: null
            };
          }
          return {
            visible: true,
            content: itemTitle,
            itemId: itemId,
            position: {
              x: event.event?.center?.x || 100,
              y: event.event?.center?.y || 100
            }
          };
        });
      }
    } else {
      // Deselected - hide tooltip
      setTooltip({
        visible: false,
        content: '',
        position: { x: 0, y: 0 },
        itemId: null
      });
    }
  };

  const handleZoomIn = () => {
    const timeline = timelineRef.current && timelineRef.current.$el;
    if (timeline) {
      timeline.zoomIn(1.0);
    }
  };

  const handleZoomOut = () => {
    const timeline = timelineRef.current && timelineRef.current.$el;
    if (timeline) {
      timeline.zoomOut(1.0);
    }
  };

  // Save window position when user interacts
  useEffect(() => {
    const timeline = timelineRef.current && timelineRef.current.$el;
    if (!timeline) return;

    const handleRangeChanged = () => {
      if (isRestoringRef.current) return;

      const range = timeline.getWindow();
      const windowState = {
        start: range.start.valueOf(),
        end: range.end.valueOf()
      };

      savedWindowRef.current = windowState;
      // ✅ Also save to localStorage for browser reload
      localStorage.setItem('chronicleWindow', JSON.stringify(windowState));

      // ✅ Notify parent to update URL (debounced via parent)
      if (onWindowChange) {
        onWindowChange(windowState);
      }
    };

    timeline.on('rangechanged', handleRangeChanged);

    return () => {
      timeline.off('rangechanged', handleRangeChanged);
    };
  }, [onWindowChange]);

  // Restore window position on load and when keyword or initialWindow changes
  useEffect(() => {
    const timeline = timelineRef.current && timelineRef.current.$el;
    if (!timeline || !isDataLoaded) return;

    const keywordChanged = prevKeywordRef.current !== keyword;
    prevKeywordRef.current = keyword;

    // ✅ Check if initialWindow prop actually changed (for internal link navigation)
    const initialWindowChanged =
      initialWindow &&
      (!prevInitialWindowRef.current ||
        prevInitialWindowRef.current.start !== initialWindow.start ||
        prevInitialWindowRef.current.end !== initialWindow.end);
    prevInitialWindowRef.current = initialWindow;

    // ✅ If initialWindow changed via link click, apply it immediately
    if (initialWindowChanged && initialWindow.start && initialWindow.end) {
      savedWindowRef.current = initialWindow;
      isRestoringRef.current = true;

      setTimeout(() => {
        if (timeline) {
          timeline.setWindow(initialWindow.start, initialWindow.end, {
            animation: false
          });
        }
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 100);
      }, 100);
      return; // Skip the rest of initialization logic
    }

    // ✅ Priority: 1) URL params, 2) localStorage (only on first load)
    if (!hasRestoredFromStorageRef.current) {
      // First check URL params (passed via initialWindow prop)
      if (
        initialWindow &&
        initialWindow.start &&
        initialWindow.end &&
        !urlWindowAppliedRef.current
      ) {
        savedWindowRef.current = initialWindow;
        urlWindowAppliedRef.current = true;
      } else {
        // Fall back to localStorage
        const savedWindow = localStorage.getItem('chronicleWindow');
        if (savedWindow) {
          try {
            const windowState = JSON.parse(savedWindow);
            savedWindowRef.current = windowState;
          } catch (e) {
            console.error('Failed to parse saved chronicle window:', e);
          }
        }
      }
      hasRestoredFromStorageRef.current = true;
    }

    // Restore window if we have saved state (either from URL, interaction, or localStorage)
    if (savedWindowRef.current) {
      isRestoringRef.current = true;

      setTimeout(() => {
        if (savedWindowRef.current && timeline) {
          timeline.setWindow(
            savedWindowRef.current.start,
            savedWindowRef.current.end,
            { animation: false }
          );
        }
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 100);
      }, 100); // Longer delay to ensure Timeline is fully initialized
    } else if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
    }
  }, [keyword, isDataLoaded, initialWindow]);

  // ✅ Updated toggle function that preserves view position
  const toggleHierarchy = () => {
    console.log('Toggle clicked');

    if (!containerRef.current) {
      console.log('Container not found!');
      return;
    }

    const timeline = timelineRef.current && timelineRef.current.$el;

    // ✅ Save current view position
    let currentWindow = null;
    if (timeline) {
      currentWindow = timeline.getWindow();
    }

    const leftPanel = containerRef.current.querySelector('.vis-panel.vis-left');
    const centerPanel = containerRef.current.querySelector(
      '.vis-panel.vis-center'
    );

    console.log('Left panel:', leftPanel);
    console.log('Center panel:', centerPanel);

    if (leftPanel && centerPanel) {
      if (isHierarchyCollapsed) {
        // Expand
        leftPanel.style.width = '';
        leftPanel.style.display = '';
        centerPanel.style.marginLeft = '';
        console.log('Expanding');
      } else {
        // Collapse
        leftPanel.style.width = '0px';
        leftPanel.style.overflow = 'hidden';
        centerPanel.style.marginLeft = '0px';
        console.log('Collapsing');
      }

      setIsHierarchyCollapsed(!isHierarchyCollapsed);

      // ✅ Restore view position after a brief delay
      if (timeline && currentWindow) {
        setTimeout(() => {
          timeline.setWindow(currentWindow.start, currentWindow.end, {
            animation: false
          });
        }, 10);
      }
    } else {
      console.log('Panels not found!');
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <div style={{ position: 'fixed', top: 70, right: 10, zIndex: 1000 }}>
        <button
          onClick={toggleHierarchy}
          style={{
            marginRight: 5,
            border: 'none',
            background: '#d3d3d3',
            cursor: 'pointer',
            padding: '5px 10px',
            borderRadius: '4px'
          }}
          title={isHierarchyCollapsed ? 'Show Groups' : 'Hide Groups'}
        >
          {isHierarchyCollapsed ? '▶' : '◀'}
        </button>
        <button
          onClick={handleZoomIn}
          style={{
            marginRight: 5,
            border: 'none',
            background: '#d3d3d3',
            cursor: 'pointer',
            padding: '5px 10px',
            borderRadius: '4px'
          }}
        >
          +
        </button>
        <button
          onClick={handleZoomOut}
          style={{
            border: 'none',
            background: '#d3d3d3',
            cursor: 'pointer',
            padding: '5px 10px',
            borderRadius: '4px'
          }}
        >
          -
        </button>
      </div>

      <Timeline
        ref={timelineRef}
        className='vis-timeline vis-item'
        id='timeline'
        options={options}
        items={items}
        groups={groups}
        events={{
          select: handleSelect
        }}
      />
      {tooltip.visible && (
        <div
          style={{
            position: 'absolute',
            top: tooltip.position.y,
            left: tooltip.position.x,
            background: 'white',
            border: '1px solid black',
            padding: '5px',
            zIndex: 1000
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default Chronicle;
